/**
 * System Health — integration detail modal — per docs/specs/system-health.md §6, §8.
 *
 * Current status + bigger 24h sparkline + recent health events (state
 * transitions) + footer actions: force re-probe, pause monitoring.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Chip } from '../components/chip';
import { Modal } from '../components/modal';
import { Sparkline } from '../components/sparkline';
import { useToast } from '../components/toast';
import { useHealthEvents } from '../../lib/api';
import type { HealthIntegration } from '../../lib/types';
import {
    formatLatency,
    formatMetric,
    formatPercent,
    getHealthSourceNote,
    healthStatusLabel,
    healthStatusTone,
    isUnavailableIntegration
} from './infra/health';

export function HealthDetailModal({
    integration,
    onClose
}: {
    integration: HealthIntegration | null;
    onClose: () => void;
}) {
    const events = useHealthEvents();
    const qc = useQueryClient();
    const { toast } = useToast();

    if (!integration) return null;

    const handleRefresh = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'integrations'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'db-pool'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'worker-rate'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'events'] })
        ]);
        toast('Health snapshot refreshed', { kind: 'info', detail: integration.name });
    };

    const myEvents =
        events.data?.filter((e) => e.integration_id === integration.id).slice(0, 20) ?? [];

    return (
        <Modal
            open={!!integration}
            onClose={onClose}
            title={integration.name}
            subtitle={integration.endpoint ?? 'internal probe'}
            width={680}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={() => void handleRefresh()}>
                        <RefreshCw size={13} /> Refresh snapshot
                    </button>
                    <button
                        className="btn btn-ghost"
                        disabled
                        title="Per-service probe controls are not wired yet"
                    >
                        Pause monitoring unavailable
                    </button>
                </>
            }
        >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                <Chip tone={healthStatusTone(integration.status)} dot>
                    {healthStatusLabel(integration.status)}
                </Chip>
                {integration.critical && <Chip tone="muted">critical</Chip>}
                <Chip tone="muted">{formatMetric(integration.req_per_min)} req/min</Chip>
                <Chip tone="muted">p95 {formatLatency(integration.p95_ms)}</Chip>
                <Chip tone={(integration.error_pct ?? 0) > 1 ? 'danger' : 'muted'}>
                    {`err ${formatPercent(integration.error_pct)}`}
                </Chip>
            </div>

            <section style={{ marginBottom: 18 }}>
                <h3
                    style={{
                        margin: '0 0 8px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4
                    }}
                >
                    24h request / error rate
                </h3>
                {isUnavailableIntegration(integration) ? (
                    <div className="empty-state">
                        <div className="title">Telemetry source unavailable</div>
                        <div className="text-muted">{getHealthSourceNote(integration)}</div>
                    </div>
                ) : (
                    <div
                        style={{
                            padding: 16,
                            background: 'var(--bg-input)',
                            border: '1px solid var(--border)',
                            borderRadius: 8,
                            color: 'var(--text-primary)'
                        }}
                    >
                        <Sparkline
                            data={integration.sparkline}
                            width={620}
                            height={96}
                            color="currentColor"
                        />
                    </div>
                )}
            </section>

            <section>
                <h3
                    style={{
                        margin: '0 0 8px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4
                    }}
                >
                    Recent health events
                </h3>
                {myEvents.length === 0 ? (
                    <div className="empty-state">
                        <div className="title">No state changes in window</div>
                    </div>
                ) : (
                    <div className="card-body tight" style={{ padding: 0 }}>
                        {myEvents.map((e) => (
                            <div
                                key={e.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '80px 1fr auto',
                                    gap: 10,
                                    padding: '8px 0',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12
                                }}
                            >
                                <span className="mono text-muted">{e.ts}</span>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ color: 'var(--text-primary)' }}>
                                        {healthStatusLabel(e.from_status)} →{' '}
                                        {healthStatusLabel(e.to_status)}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                        {e.note}
                                    </div>
                                </div>
                                <Chip tone={healthStatusTone(e.to_status)} dot>
                                    {healthStatusLabel(e.to_status)}
                                </Chip>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <div className="form-hint" style={{ marginTop: 16 }}>
                Recent state transitions are persisted from worker snapshots. Per-service
                pause/disable controls are still planned, so this modal remains read-only.
            </div>
        </Modal>
    );
}
